import { useState, type ReactElement } from 'react';
import { api, type TemplateView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { codeLabel } from '../shared/lib/status';
import { useAsync } from '../shared/lib/use-async';
import {
  Card,
  EmptyState,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';

/**
 * Шаблоны СМС (ТЗ разд. 13, FR-593, FR-588).
 *
 * Текст сообщения — операционная настройка, а не код: его правит администратор без
 * деплоя. Поэтому экран, а не миграция.
 *
 * Счётчик сегментов на видном месте не для красоты: кириллическая СМС вмещает 70
 * символов в сегмент, и лишняя строка в шаблоне превращает каждое уведомление в два
 * сообщения — это прямые ежемесячные расходы заказчика.
 *
 * Языки показаны вкладками и ведутся раздельно (FR-558): узбекский текст — не перевод
 * русского на лету, а свой текст, и подменять один другим нельзя.
 */

const LANGUAGES = ['uz-Latn', 'ru'] as const;

export function TemplatesPage({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const messages = useMessages();
  const [language, setLanguage] = useState<string>(LANGUAGES[0]);
  const [saved, setSaved] = useState<string | null>(null);

  const templates = useAsync(() => api.notificationTemplates(), []);
  const rows = (templates.data ?? []).filter((template) => template.language === language);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.templates.title}</h1>

      <p style={{ color: 'var(--text-muted)' }}>{messages.cabinet.templates.hint}</p>

      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        {LANGUAGES.map((code) => (
          <button
            key={code}
            type="button"
            style={code === language ? primaryButtonStyle : secondaryButtonStyle}
            onClick={() => {
              setLanguage(code);
              setSaved(null);
            }}
          >
            {code === 'ru' ? messages.languages.ru : messages.languages.uzLatn}
          </button>
        ))}
      </div>

      {saved !== null && <Card tone="ok">{saved}</Card>}

      {rows.length === 0 ? (
        <EmptyState text={messages.cabinet.common.empty} />
      ) : (
        rows.map((template) => (
          <TemplateEditor
            key={`${template.template_code}:${template.language}`}
            template={template}
            canEdit={canEdit}
            onSaved={() => {
              setSaved(messages.cabinet.templates.saved);
              templates.reload();
            }}
          />
        ))
      )}
    </div>
  );
}

function TemplateEditor({
  template,
  canEdit,
  onSaved,
}: {
  readonly template: TemplateView;
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  const messages = useMessages();
  const [body, setBody] = useState(template.body);
  const [enabled, setEnabled] = useState(template.is_enabled);

  const dirty = body !== template.body || enabled !== template.is_enabled;
  const segments = countSegments(body);

  return (
    <Card tone={segments > 1 ? 'warn' : undefined}>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'baseline' }}>
        <strong style={{ flex: 1 }}>
          {codeLabel(messages.cabinet.templates.points, template.template_code)}
        </strong>

        <span className="num">{`${messages.cabinet.templates.length}: ${String(body.length)}`}</span>
        <span className="num" style={{ color: segments > 1 ? 'var(--warn)' : 'var(--text-muted)' }}>
          {`${messages.cabinet.templates.segments}: ${String(segments)}`}
        </span>
      </div>

      <textarea
        value={body}
        rows={3}
        disabled={!canEdit}
        onChange={(event) => setBody(event.target.value)}
        style={{
          width: '100%',
          font: 'inherit',
          padding: 'var(--sp-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-sm)',
          background: 'var(--surface-sunken)',
          color: 'var(--text)',
        }}
      />

      <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-caption)' }}>
        {messages.cabinet.templates.placeholders}
      </span>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
        <label style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canEdit}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          {messages.cabinet.templates.enabled}
        </label>

        {canEdit && dirty && (
          <button
            type="button"
            style={primaryButtonStyle}
            onClick={() => {
              void api
                .saveTemplate({
                  template_code: template.template_code,
                  language: template.language,
                  body,
                  is_enabled: enabled,
                })
                .then(onSaved);
            }}
          >
            {messages.cabinet.templates.save}
          </button>
        )}
      </div>
    </Card>
  );
}

/** Пределы сегмента: UCS-2 для кириллицы, GSM-7 для латиницы (FR-588). */
const UCS2_SINGLE = 70;
const UCS2_MULTI = 67;
const GSM7_SINGLE = 160;
const GSM7_MULTI = 153;
/** Граница ASCII: символ за ней переводит всё сообщение в UCS-2. */
const ASCII_MAX = 127;

/**
 * Число сегментов сообщения.
 *
 * Кириллица кодируется UCS-2: 70 символов в одиночном сообщении и 67 в каждом сегменте
 * составного. Латиница — GSM-7: 160 и 153 соответственно. Счёт по фактическому
 * алфавиту текста, а не по локали: узбекский шаблон с одним русским словом целиком
 * уезжает в UCS-2, и это ровно тот случай, который удивляет при первом счёте.
 */
export function countSegments(body: string): number {
  const unicode = [...body].some((char) => (char.codePointAt(0) ?? 0) > ASCII_MAX);
  const single = unicode ? UCS2_SINGLE : GSM7_SINGLE;
  const multi = unicode ? UCS2_MULTI : GSM7_MULTI;

  if (body.length === 0) {
    return 0;
  }

  return body.length <= single ? 1 : Math.ceil(body.length / multi);
}
